<template>
	<div ref="editorcontainer" class="editor">
		<slot name="toolbar"></slot>
		<div ref="editor" spellcheck="true"></div>
	</div>
</template>

<script>
import api from '@/api/tutorial_editor'
import Quill from 'quill'
import { mapState } from 'vuex'

import Sidebar from '@/quill/Sidebar'
import VideoEmbed from '@/quill/VideoEmbed'
import ImageEmbed from '@/quill/ImageEmbed'
import EmbedSelect from '@/quill/EmbedSelect'
import InsertPlaceholder from '@/quill/Placeholder'
import generateEmbedUrl from '@/quill/parser'

Quill.register({
	'formats/image': ImageEmbed,
	'modules/sidebar': Sidebar,
	'modules/videoembed': VideoEmbed,
	'modules/embedselect': EmbedSelect
}, true)

export default {
	name: 'editor',
	data () {
		return {
			innerContent: '',
			defaultModules: {
				videoembed: true,
				embedselect: true,
				sidebar: {
					handlers: {
						'image': this.imageHandler,
						'video': this.videoHandler,
						'bullet-list': this.bulletListHandler
					}
				},
				toolbar: ['bold', 'italic', 'link']
			},
			contentMaxLength: Grabcad.AttributeLimits.Generic.CONTENT_BODY,
		}
	},
	props: {
		value: String,
		options: {
			type: Object,
			required: false,
			default () {
				return {}
			}
		}
	},
	mounted () {
		this.initialize()
	},
	beforeDestroy () {
		this.quill = null
	},
	computed: {
		...mapState(['tutorial'])
	},
	methods: {
		videoHandler () {
			InsertPlaceholder(this.quill, 'Paste Youtube or Vimeo link here and press Enter')
		},

		imageHandler (file) {
			let blobUrl = URL.createObjectURL(file)
			let range = this.quill.getSelection(true)
			this.quill.insertEmbed(range.index, 'image', blobUrl)
			this.quill.insertText(range.index + 1, '\n\n')

			const fileStatus = {name: blobUrl, status: 'Uploading file'}
			this.$store.commit('startLoading', fileStatus)

			this.uploadFile(file, blobUrl).then(() => {
				this.$store.commit('finishLoading', fileStatus)
			}).catch(() => {
				this.$store.dispatch('setUploadFailedFlash')
				this.$store.commit('finishLoading', fileStatus)
			});
		},

		bulletListHandler () {
			this.quill.format('list', 'list', Quill.sources.USER)
		},

		uploadFile (file, blobUrl) {
			let payload = new FormData()
			payload.append('file', file)
			payload.append('blob_url', blobUrl)
			return api.uploadFile({id: this.tutorial.id}, payload)
		},

		initialize () {
			if (this.$el) {
				this.options.theme = 'bubble'
				this.options.boundary = this.$refs.editorcontainer
				this.options.modules = this.defaultModules
				this.options.modules.toolbar = this.defaultModules.toolbar
				this.options.placeholder = this.options.placeholder || 'Instructions for this step...'
				this.options.formats = [
					'bold',
					'italic',
					'link',
					'image',
					'embeddedvideo',
					'list'
				]
				this.options.readOnly = this.options.readOnly !== undefined ? this.options.readOnly : false
				this.quill = new Quill(this.$refs.editor, this.options)

				if (this.value) {
					this.quill.pasteHTML(this.value)
				}

				this.quill.clipboard.addMatcher(Node.TEXT_NODE, function (node, delta) {
					const embed = generateEmbedUrl(node.data)
					const LINK_REGEXP = /^(https?:\/\/[\S]+)$/
					let onlyLink = node.data.match(LINK_REGEXP)
					if (embed && onlyLink) {
						delta.ops = [{insert: {'embeddedvideo': embed}}]
					}
					return delta
				})

				this.quill.on('selection-change', (range) => {
					if (!range) {
						this.$emit('blur', this.quill)
					} else {
						this.$emit('focus', this.quill)
					}
				})

				this.quill.on('text-change', () => {
					const truncLength = this.quill.root.innerHTML.length - this.contentMaxLength
					if (truncLength > 0) {
						this.quill.deleteText(this.quill.getLength() - truncLength - 1, truncLength, 'silent')
					}
					let html = this.$refs.editor.children[0].innerHTML
					if (html === '<p><br></p>') html = ''
					this.innerContent = html
					this.$emit('input', this.innerContent)
					this.$emit('change', {
						editor: this.quill,
						html: html
					})
				})

				this.$emit('ready', this.quill)
			}
		}
	},
	watch: {
		'value' (newValue) {
			if (this.quill) {
				if (newValue !== this.innerContent) {
					this.innerContent = newValue
					this.quill.pasteHTML(newValue)
				} else if (!newValue) {
					this.quill.setText('')
				}
			}
		}
	}
}
</script>

<style src="quill/dist/quill.core.css"></style>
<style src="quill/dist/quill.bubble.css"></style>
<style lang="scss" src="@/styles/tutorial_editor/quill-sidebar.scss"></style>

<style lang="scss">
@import "@/styles/tutorial_editor/globals";

#tutorialEditorApp {
	.ql-container {
		font-size: 1rem;
		font-family: 'Open Sans', sans-serif;
	}

	.ql-editor {
		padding: 0;
		min-height: 25vh;
	}

	.ql-editor p {
		margin-bottom: 10px;
	}

	.ql-bubble .ql-tooltip {
		background-color: $black-gray;
		border-radius: 4px;
	}

	.ql-editor .embeddedimage {
		display: block;
		margin: 0 auto;
		cursor: default;
	}

	.ql-editor .embeddedimage, .ql-editor .embeddedvideo {
		border: solid 3px transparent;
	}

	.ql-editor .is-selected {
		border: solid 3px $red;
	}

	.ql-bubble .ql-tooltip:not(.ql-flip) .ql-tooltip-arrow {
		border-bottom-color: $black-gray;
	}

	.ql-bubble .ql-toolbar .ql-formats:first-child {
		margin-left: 8px;
	}

	.ql-bubble .ql-toolbar .ql-formats {
		margin: 8px;
	}

	.ql-tooltip {
		width: auto !important;
	}

	.ql-editor.ql-blank::before {
		left: 0;
		font-style: normal;
		color: rgba(0,0,0,0.4);
	}

	.editor {
		width: 100%;
		.embeddedvideo {
			display: block;
			overflow: hidden;
			position: relative;
			padding-bottom: 56.34%;

			> iframe {
				width: 100%;
				height: 100%;
				position: absolute;
				pointer-events: none;
			}
		}

		.ql-editor:not(.ql-blank) {
			p[data-placeholder] {
				&:before {
					font-style: normal;
					color: rgba(0, 0, 0, 0.4);
					pointer-events: none;
					position: absolute;
					content: attr(data-placeholder);
				}
			}
		}
	}
}
</style>
